import type { CheckboxChangeFn } from '../../components/Checkbox/Checkbox';
import type {
  DataGridColumnProps,
  DataGridGetRowLinkOptionsFn,
} from '../../components/DataGrid/DataGrid';
import type { DataGridColumnOptionsProps } from '../../components/DataGridControl/DataGridControl';
import type { GridFilters } from '../../components/GridFiltersManager/filterDefinitions';
import type { ApiRequestObject } from '../../lib/flotiq-client/types/api-request';
import type { SortContentObjectsLocalStorage } from '../../types/localStorage';

import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import {
  getTestProps,
  prepareColumns,
  setInitialGridOptions,
} from '../../lib/helpers';

import Checkbox from '../../components/Checkbox/Checkbox';
import CustomizableDataGrid from '../../components/CustomizableDataGrid/CustomizableDataGrid';
import { useGridFilters } from '../../components/DataGrid/useGridFilters';
import DataGridControl from '../../components/DataGridControl/DataGridControl';
import GridFiltersManager from '../../components/GridFiltersManager/GridFiltersManager';
import Heading from '../../components/Heading/Heading';
import Loader from '../../components/Loader/Loader';
import StatusBar from '../../components/StatusBar/StatusBar';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarButton from '../../components/Topbar/buttons/base/TopbarButton';

import { useAllPlans } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';

import UserContext from '../../contexts/UserContext';

import { WarningIcon } from '../../images/shapes';

import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';
import { getDefinedColumns } from './definedColumns';

const parseFilters = (filters: GridFilters) => {
  const parsedFilters: ApiRequestObject = {};

  Object.keys(filters || {}).forEach((key) => {
    if (filters[key].filter === 'false') {
      parsedFilters[key] = 0;
    } else if (filters[key].filter === 'true') {
      parsedFilters[key] = 1;
    } else {
      parsedFilters[key] = filters[key].filter;
    }
  });

  return parsedFilters;
};

const testId = 'plans';

const Plans = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const OPTIONS_KEY = 'cms.plans-grid-state';
  const SORT_KEY = 'cms.plans-sort';

  const { isRoleAdmin } = use(UserContext);

  const plansGridContainer = useRef<HTMLDivElement>(null);

  const [initDataHasContent, setInitDataHasContent] = useState(false);

  const onlyVisible = !searchParams.get('hidden');
  const onOnlyVisibleChange = useCallback<CheckboxChangeFn>(
    (e) => {
      const visible = e.target.checked;
      setSearchParams((params) => {
        if (visible) params.delete('hidden');
        else params.set('hidden', `true`);
        return params;
      });
    },
    [setSearchParams],
  );

  const [columns, setColumns] = useState<DataGridColumnProps[]>([]);
  const [sort, setSort, { removeItem: removeSort }] =
    useLocalStorageState<SortContentObjectsLocalStorage>(SORT_KEY);
  const [gridOptions, setGridOptions, { removeItem: removeGridOptions }] =
    useLocalStorageState<DataGridColumnOptionsProps[]>(OPTIONS_KEY);

  const {
    filters,
    handleFiltersChange,
    page,
    handlePageChange,
    limit,
    handleLimitChange,
    removeAllFilters,
  } = useGridFilters('plans');

  const plansParams = useMemo(
    () => ({
      page: page,
      limit,
      ...parseFilters(filters),
      ...(onlyVisible ? { visible: 1 } : {}),
      ...(sort?.sortOrder ? { order_direction: sort?.sortOrder } : {}),
      ...(sort?.sortField ? { order_by: sort?.sortField } : {}),
    }),
    [page, limit, filters, onlyVisible, sort?.sortOrder, sort?.sortField],
  );

  const allPlansOptions = useMemo(
    () => ({
      pause: !isRoleAdmin,
    }),
    [isRoleAdmin],
  );

  const {
    data: allPlans,
    isLoading: allPlansAreLoading,
    pagination: allPlansPagination,
    errors: allPlansErrors,
    reload: allPlansReload,
  } = useAllPlans(plansParams, allPlansOptions);

  useApiErrorsToast(allPlansErrors);

  useEffect(() => {
    if (allPlans.length && !initDataHasContent) {
      setInitDataHasContent(true);
    }
  }, [allPlans.length, initDataHasContent]);

  useEffect(() => {
    if (allPlansPagination?.total_pages < page)
      handlePageChange(allPlansPagination.total_pages);
  }, [handlePageChange, page, allPlansPagination?.total_pages]);

  const noAccess = useMemo(() => {
    return (
      <Heading
        level={2}
        additionalClasses="text-3xl md:text-4xl leading-8 dark:text-slate-200"
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'no-access')}
        >
          <WarningIcon className="text-red w-14 md:w-20 mb-3" />
          {t('Global.NoAccess')}
        </div>
      </Heading>
    );
  }, [t]);

  const emptyResult = useMemo(() => {
    if (!isRoleAdmin) {
      return noAccess;
    }
    if (initDataHasContent) return null;
    if (allPlansAreLoading) {
      return (
        <Loader
          size="small"
          type="spinner-grid"
          {...getTestProps(testId, 'loader', 'testId')}
        />
      );
    }
    return (
      <Heading
        level={2}
        additionalClasses={'text-3xl md:text-4xl leading-8 dark:text-slate-200'}
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'empty-data')}
        >
          <WarningIcon
            className="text-red w-14 md:w-20 mb-3"
            title={t('Plans.CouldntFind')}
          />
          {t('Plans.CouldntFind')}
        </div>
      </Heading>
    );
  }, [isRoleAdmin, initDataHasContent, allPlansAreLoading, t, noAccess]);

  const handleResetGrid = useCallback(() => {
    setSort(undefined);
    removeAllFilters();
    removeSort();
    removeGridOptions();
    setGridOptions(undefined);
  }, [
    removeAllFilters,
    removeGridOptions,
    removeSort,
    setGridOptions,
    setSort,
  ]);

  const handleInitialGridOptions = useCallback(
    (cols: DataGridColumnProps[], minWidth: number) => {
      setInitialGridOptions(
        cols,
        minWidth,
        (plansGridContainer.current?.offsetWidth || 0) - 58,
        setGridOptions,
        setColumns,
      );
    },
    [setGridOptions],
  );

  useEffect(() => {
    prepareColumns(
      gridOptions,
      t,
      testId,
      setGridOptions,
      handleInitialGridOptions,
      setColumns,
      getDefinedColumns,
    );
  }, [gridOptions, handleInitialGridOptions, setGridOptions, t]);

  const getRowLinkOptions = useCallback<DataGridGetRowLinkOptionsFn>(
    (_, rowData) => ({ to: `/plans/edit/${rowData.id}` }),
    [],
  );

  return (
    <PageLayout
      page="admin/plans"
      menuItemOpen="admin"
      title={t('Global.Plans')}
      breadcrumbs={<TopbarBreadcrumbs />}
      buttons={
        isRoleAdmin ? (
          <TopbarButton label={t('Plans.Form.AddPlan')} link={'/plans/add'} />
        ) : null
      }
      secondaryTopbar={
        initDataHasContent ? (
          <DataGridControl
            handleResetGrid={handleResetGrid}
            limit={limit}
            handleLimitChange={handleLimitChange}
            gridOptions={gridOptions}
            setGridOptions={setGridOptions}
            testId={testId}
          />
        ) : null
      }
      testId={testId}
      noPaddings
    >
      <div className={predefinedLayoutClasses.withDataGrid}>
        {isRoleAdmin && (
          <Checkbox
            name="visible"
            checked={onlyVisible}
            onChange={onOnlyVisibleChange}
            label={t('Plans.DisplayVisible')}
            additionalContainerClasses="py-2"
            additionalLabelClasses={twMerge('ml-2 text-sm md:text-base mr-2')}
            additionalCheckboxClasses="mt-0 lg:mt-0.5 h-4 md:h-5 w-4 md:w-5
                bg-size-[8px_8px] md:bg-size-[10px_10px] bg-white border-slate-300 checked:border-blue"
            {...getTestProps(testId, 'only-visible', 'testId')}
          />
        )}
        <div
          className={twMerge(
            predefinedLayoutClasses.dataGridContainer,
            isRoleAdmin && 'mt-2 lg:mt-2',
          )}
          ref={plansGridContainer}
        >
          {initDataHasContent ? (
            <>
              <GridFiltersManager
                columns={columns}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                showSearchInput={false}
                showOperators={false}
                testId={testId}
              />
              <CustomizableDataGrid
                columns={columns}
                setColumns={setColumns}
                data={allPlans}
                isLoading={allPlansAreLoading}
                sort={sort?.sortField}
                sortOrder={sort?.sortOrder}
                sortingLocalStorageKey={SORT_KEY}
                optionsLocalStorageKey={OPTIONS_KEY}
                setCurrentPage={handlePageChange}
                statusBar={
                  <StatusBar
                    rows={allPlansPagination.count}
                    currentPage={page}
                    pagesCount={allPlansPagination.total_pages}
                    handlePageChange={handlePageChange}
                    handleDataUpdate={allPlansReload}
                    resultsFrom={
                      (allPlansPagination.current_page - 1) * limit + 1
                    }
                    resultsTo={
                      (allPlansPagination.current_page - 1) * limit +
                      allPlansPagination.count
                    }
                    resultsTotalCount={allPlansPagination.total_count}
                    {...getTestProps(testId, 'statusbar', 'testId')}
                  />
                }
                noDataInfoText={t('Plans.FiltersEmptyResult')}
                getRowLinkOptions={getRowLinkOptions}
                {...getTestProps(testId, 'grid', 'testId')}
              />
            </>
          ) : (
            <div className={predefinedLayoutClasses.whiteBox}>
              {emptyResult}
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default Plans;
